import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuthStore from "../../store/auth";
import useNotifications from "../../hooks/useNotifications";

const NAV = [
  { path: "/dashboard",  icon: "📊", key: "dashboard" },
  { path: "/income",     icon: "📈", key: "income" },
  { path: "/expenses",   icon: "📉", key: "expenses" },
  { path: "/budgets",    icon: "🎯", key: "budgets" },
  { path: "/categories", icon: "🏷️", key: "categories" },
  { path: "/reports",    icon: "📋", key: "reports" },
];
const NAV_FAMILY = [{ path: "/family", icon: "👨‍👩‍👧", key: "family" }];

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { user, logout, isPremium, isTrialActive, trialDaysLeft, isAdmin } = useAuthStore();
  const { notifications, unread, markRead } = useNotifications();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotif, setShowNotif]     = useState(false);

  const trialDays     = trialDaysLeft();
  const showBanner    = !isPremium() && isTrialActive() && trialDays <= 5;
  const trialExpired  = !isPremium() && !isTrialActive();
  const isFamily      = user?.account_type === "family";

  const closeSidebar = () => setSidebarOpen(false);

  const changeLang = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("bf_language", lang);
  };

  const navStyle = (active) => ({
    display: "flex", alignItems: "center", gap: 12, padding: "11px 12px",
    borderRadius: 10, color: active ? "#7c6af7" : "#9090b0",
    background: active ? "#7c6af720" : "transparent",
    textDecoration: "none", fontSize: 14, fontWeight: 500,
    marginBottom: 2, border: "none", width: "100%", textAlign: "left", cursor: "pointer",
  });

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div style={{ padding: "20px 20px 16px", fontFamily: "Syne,sans-serif", fontSize: 22, fontWeight: 800, background: "linear-gradient(135deg,#7c6af7,#f7c26a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", borderBottom: "1px solid var(--border)" }}>
        BudgetFlow
      </div>

      {/* User info */}
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid var(--border)" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#7c6af7,#f7c26a)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne,sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", flexShrink: 0 }}>
          {user?.name?.[0]?.toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.name}</div>
          <div style={{ fontSize: 12, color: "#9090b0" }}>
            {isPremium() ? "⭐ Premium" : isTrialActive() ? `🕐 ${trialDays}j` : "❌ Expiré"}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto" }}>
        {NAV.map(item => (
          <NavLink key={item.path} to={item.path} style={({ isActive }) => navStyle(isActive)} onClick={closeSidebar}>
            <span>{item.icon}</span><span>{t(item.key)}</span>
          </NavLink>
        ))}
        {isFamily && NAV_FAMILY.map(item => (
          <NavLink key={item.path} to={item.path} style={({ isActive }) => navStyle(isActive)} onClick={closeSidebar}>
            <span>{item.icon}</span><span>{t(item.key)}</span>
          </NavLink>
        ))}
        {isAdmin() && (
          <NavLink to="/admin" style={({ isActive }) => navStyle(isActive)} onClick={closeSidebar}>
            <span>🛡️</span><span>Admin</span>
          </NavLink>
        )}
      </nav>

      {/* Bottom */}
      <div style={{ padding: "12px 8px", borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 8 }}>
        {!isPremium() && (
          <button onClick={() => { navigate("/pricing"); closeSidebar(); }}
            style={{ padding: "10px 12px", background: "linear-gradient(135deg,#7c6af7,#f7c26a)", color: "#fff", border: "none", borderRadius: 10, fontFamily: "Syne,sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            ⭐ {t("upgrade")}
          </button>
        )}
        <button onClick={() => { logout(); navigate("/login"); }}
          style={{ padding: "10px 12px", background: "var(--surface2)", color: "#9090b0", border: "1px solid var(--border)", borderRadius: 10, fontSize: 14, cursor: "pointer" }}>
          🚪 {t("logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="app-layout">
      {/* Overlay mobile */}
      <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={closeSidebar} />

      {/* Sidebar */}
      <aside className={`app-sidebar ${sidebarOpen ? "open" : ""}`}>
        <SidebarContent />
      </aside>

      {/* Main */}
      <main className="app-main">
        {/* Header */}
        <header className="app-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Hamburger — visible sur mobile/tablette */}
            <button className="hamburger" onClick={() => setSidebarOpen(v => !v)} aria-label="Menu">
              {sidebarOpen ? "✕" : "☰"}
            </button>
            {/* Logo visible sur mobile dans le header */}
            <div style={{ fontFamily: "Syne,sans-serif", fontSize: 18, fontWeight: 800, background: "linear-gradient(135deg,#7c6af7,#f7c26a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
              className="show-mobile">
              BudgetFlow
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Notifications — famille uniquement */}
            {isFamily && (
              <div style={{ position: "relative" }}>
                <button onClick={() => { setShowNotif(v => !v); markRead(); }}
                  style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--surface2)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer", position: "relative", minHeight: "auto" }}>
                  🔔
                  {unread > 0 && (
                    <span style={{ position: "absolute", top: -4, right: -4, background: "var(--danger)", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </button>
                {showNotif && (
                  <div className="notif-panel" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "0 8px 32px #00000040", zIndex: 300 }}>
                    <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, fontSize: 14 }}>Notifications</span>
                      <button onClick={() => setShowNotif(false)} style={{ background: "none", border: "none", color: "var(--text3)", cursor: "pointer", minHeight: "auto" }}>✕</button>
                    </div>
                    <div style={{ maxHeight: 280, overflowY: "auto" }}>
                      {notifications.length === 0 ? (
                        <p style={{ padding: 20, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>Aucune activité</p>
                      ) : notifications.map(n => (
                        <div key={n.id} style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontWeight: 600 }}>{n.member_name}</span>
                            <span style={{ color: "var(--text3)", fontSize: 11 }}>{new Date(n.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                          <div style={{ color: "var(--text2)" }}>
                            <span style={{ color: "var(--danger)", fontWeight: 600 }}>-{new Intl.NumberFormat("fr-FR").format(Math.round(n.amount))}</span>
                            {" · "}{n.category_icon} {n.category_name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Langue */}
            <select value={i18n.language} onChange={e => changeLang(e.target.value)}
              style={{ background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text)", padding: "7px 10px", borderRadius: 10, fontSize: 13, cursor: "pointer", width: "auto" }}>
              <option value="fr">🇫🇷 FR</option>
              <option value="en">🇬🇧 EN</option>
            </select>

            {/* Avatar */}
            <NavLink to="/profile" style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#7c6af7,#f7c26a)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, color: "#fff", textDecoration: "none", minHeight: "auto" }}>
              {user?.name?.[0]?.toUpperCase()}
            </NavLink>
          </div>
        </header>

        {/* Bannière essai expiré */}
        {trialExpired && (
          <div className="banner" style={{ background: "linear-gradient(135deg,#f76a6a,#dd3333)" }}>
            <div>
              <div style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, fontSize: 15, color: "#fff" }}>⏰ {t("trial_expired")} !</div>
              <div style={{ fontSize: 13, color: "#ffcccc" }}>{t("upgrade_message")}</div>
            </div>
            <button onClick={() => navigate("/pricing")} style={{ padding: "9px 20px", background: "#fff", color: "#f76a6a", border: "none", borderRadius: 10, fontFamily: "Syne,sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              {t("upgrade")}
            </button>
          </div>
        )}

        {/* Bannière essai bientôt expiré */}
        {showBanner && (
          <div className="banner" style={{ background: "linear-gradient(135deg,#f7a000,#e68800)" }}>
            <div style={{ fontSize: 14, color: "#fff", fontWeight: 600 }}>
              ⚠️ {trialDays} {t("trial_days_left")} !
            </div>
            <button onClick={() => navigate("/pricing")} style={{ padding: "8px 18px", background: "#fff", color: "#e68800", border: "none", borderRadius: 8, fontFamily: "Syne,sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              {t("upgrade")}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}